import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { supabase } from '../../core/supabase/supabase.client';

export interface DeliveryAreaPoint {
  lat: number;
  lng: number;
}

export interface Store {
  id: number;
  name: string;
  store_code: string;
  address: string;
  city: string;
  store_radius: number;
  delivery_area: DeliveryAreaPoint[] | null;
  latitude: number | null;
  longitude: number | null;
  opening_time: string | null;
  closing_time: string | null;
  contact_number: string | null;
  email: string | null;
  gst_number: string | null;
  legal_business_name: string | null;
  fssai_license_number: string | null;
  pan_number: string | null;
  weather_status?: StoreWeatherStatus;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}
export type StoreWeatherStatus = 'normal' | 'sunny' | 'cloudy' | 'rain';
export interface StoreOption {
  id: number;
  name: string;
  store_code?: string;
  city?: string;
  is_active?: boolean;
  weather_status?: StoreWeatherStatus;
}

export interface StoreClosure {
  id: number;
  store_id: number;
  title: string;
  starts_at: string;
  ends_at: string;
  is_cancelled: boolean;
  created_at: string;
  is_current?: boolean;
  is_upcoming?: boolean;
}

export interface StoreOrderStatus {
  accepting: boolean;
  reason: 'open' | 'outside_hours' | 'holiday' | 'inactive' | string;
  message: string | null;
  next_accepting_at: string | null;
  closure_title: string | null;
}
@Injectable({ providedIn: 'root' })
export class StoreService {
  private table = 'stores';
  async createStore(payload: Partial<Store>) {
    const insertPayload: Partial<Store> = {
      name: payload.name,
      store_code: payload.store_code,
      address: payload.address,
      city: payload.city,
      store_radius: payload.store_radius ?? 0,
      delivery_area: payload.delivery_area ?? null,
      latitude: payload.latitude ?? null,
      longitude: payload.longitude ?? null,
      is_active: payload.is_active ?? true,
      opening_time: payload.opening_time ?? null,
      closing_time: payload.closing_time ?? null,
      contact_number: payload.contact_number ?? null,
      email: payload.email ?? null,
      gst_number: payload.gst_number ?? null,
      legal_business_name: payload.legal_business_name ?? null,
      fssai_license_number: payload.fssai_license_number ?? null,
      pan_number: payload.pan_number ?? null,
      weather_status: payload.weather_status ?? 'normal',
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from(this.table)
      .insert(insertPayload)
      .select()
      .single();

    if (error) throw error;
    return data as Store;
  }

  // ✅ Update store
  async updateStore(id: number, payload: Partial<Store>) {
    const updatePayload: Partial<Store> = {
      name: payload.name,
      store_code: payload.store_code,
      address: payload.address,
      city: payload.city,
      store_radius: payload.store_radius,
      delivery_area: payload.delivery_area,
      latitude: payload.latitude,
      longitude: payload.longitude,
      opening_time: payload.opening_time,
      closing_time: payload.closing_time,
      contact_number: payload.contact_number,
      email: payload.email,
      gst_number: payload.gst_number,
      legal_business_name: payload.legal_business_name,
      fssai_license_number: payload.fssai_license_number,
      pan_number: payload.pan_number,
      weather_status: payload.weather_status,
      updated_at: new Date().toISOString(),
    };

    if (typeof payload.is_active === 'boolean') {
      updatePayload.is_active = payload.is_active;
    }

    const { error } = await supabase
      .from(this.table)
      .update(updatePayload)
      .eq('id', id);

    if (error) throw error;
  }

  // ✅ Soft delete store
  async deleteStore(id: number) {
    const { error } = await supabase
      .from('stores')
      .update({
        is_active: false,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) throw error;
  }
  // ✅ GET ALL STORES (pagination + search + sort)
  getAllStores(
    page = 1,
    limit = 10,
    search = '',
    sortBy: keyof Store = 'created_at',
    order: 'ASC' | 'DESC' = 'DESC',
    status: 'active' | 'inactive' | 'all' = 'active'
  ): Observable<{ data: Store[]; count: number }> {
    const fromIndex = (page - 1) * limit;
    const toIndex = fromIndex + limit - 1;

    let query = supabase
      .from(this.table)
      .select('*', { count: 'exact' })
      .range(fromIndex, toIndex)
      .order(sortBy, { ascending: order === 'ASC' });

    if (search) {
      query = query.ilike('name', `%${search}%`);
    }
    if (status !== 'all') query = query.eq('is_active', status === 'active');

    return from(
      query.then(({ data, count, error }) => {
        if (error) throw error;
        return {
          data: (data ?? []) as Store[],
          count: count ?? 0,
        };
      })
    );
  }

  // ✅ GET SINGLE STORE
  getSingleStore(id: number): Observable<Store> {
    return from(
      supabase
        .from('stores')
        .select('*')
        .eq('id', id)
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return data as Store;
        })
    );
  }
  allStores(): Observable<StoreOption[]> {
    return from(
      supabase
        .from(this.table)
        .select('id, name, store_code, city, is_active, weather_status')
        .eq('is_active', true)
        .order('name', { ascending: true })
        .then(({ data, error }) => {
          if (error) throw error;
          return data ?? [];
        })
    );
  }

  async updateWeatherStatus(id: number, weatherStatus: StoreWeatherStatus) {
    const { error } = await supabase.rpc('admin_update_store_weather', {
      p_store_id: id,
      p_weather_status: weatherStatus,
    });

    if (error) throw error;
  }

  async listStoreClosures(storeId: number): Promise<StoreClosure[]> {
    const { data, error } = await supabase.rpc('admin_list_store_closures', {
      p_store_id: storeId,
    });
    if (error) throw error;
    return Array.isArray(data) ? (data as StoreClosure[]) : [];
  }

  async createStoreClosure(params: {
    storeId: number;
    title: string;
    startsAt: string;
    endsAt: string;
  }): Promise<void> {
    const { error } = await supabase.rpc('admin_create_store_closure', {
      p_store_id: params.storeId,
      p_title: params.title,
      p_starts_at: params.startsAt,
      p_ends_at: params.endsAt,
    });
    if (error) throw error;
  }

  async cancelStoreClosure(closureId: number): Promise<void> {
    const { error } = await supabase.rpc('admin_cancel_store_closure', {
      p_closure_id: closureId,
    });
    if (error) throw error;
  }

  async currentlyClosedStoreIds(): Promise<number[]> {
    const { data, error } = await supabase.rpc(
      'admin_currently_closed_store_ids'
    );
    if (error) throw error;
    return Array.isArray(data) ? data.map((id) => Number(id)) : [];
  }
}
